import {
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as EyeCloseIcon } from "../../assets/icons/eyeClose.svg";
import { ReactComponent as EyeOpenIcon } from "../../assets/icons/eyeOpen.svg";
import ModalTickIcon from "../../assets/icon_components/Tickmark";
import { useUserAccountLoginMutation } from "../../features/login/loginapi.services";
import {
  resetUserInfo,
  updateInfo,
  updateUserInfo,
} from "../../services/loginInfo";
import { MuiCustomModal } from "../../shared/Modal/MuiCustomModal";
import { selectedTheme, tenants, user, userStatus } from "../../utils/constant";
import { LoginFields } from "../../utils/loginFields";
import SchedulerLogo from "../../assets/icons/scheduler360Logo.svg"

const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_RATE_LIMIT_MS = 2 * 60 * 1000;
const LOGIN_ATTEMPT_STORAGE_KEY = "login_attempt_times";

const Login = () => {

  const getStoredAttemptTimes = () => {
    try {
      const raw = localStorage.getItem(LOGIN_ATTEMPT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((time) => Number.isFinite(time)) : [];
    } catch {
      return [];
    }
  };

  // Internal State
  const [remeberPassword, setRememberPassword] = React.useState(false);
  const [passwordVisibility, setPasswordVisibility] = React.useState(false);
  const [verificationPopup, setVerificationPopup] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginAttemptTimes, setLoginAttemptTimes] = React.useState(getStoredAttemptTimes);
  const [currentTime, setCurrentTime] = React.useState(Date.now());

  const location = useLocation();
  const handleClosePopup = () => {
    setVerificationPopup(false);
    navigate("/login");
  };
  // Redux

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userInfo);

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    clearErrors,
  } = useForm();

  const [userAccountLogin] = useUserAccountLoginMutation();

  const loginRateLimitInfo = React.useMemo(() => {
    if (!loginAttemptTimes.length) {
      return { isBlocked: false, remainingSeconds: 0 };
    }

    const recentAttempts = loginAttemptTimes.filter(
      (time) => currentTime - time < LOGIN_RATE_LIMIT_MS,
    );

    if (recentAttempts.length < MAX_LOGIN_ATTEMPTS) {
      return { isBlocked: false, remainingSeconds: 0 };
    }

    const oldestAttempt = recentAttempts[0];
    const remainingMs = LOGIN_RATE_LIMIT_MS - (currentTime - oldestAttempt);
    const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000));

    return { isBlocked: true, remainingSeconds };
  }, [currentTime, loginAttemptTimes]);

  React.useEffect(() => {
    if (!loginRateLimitInfo.isBlocked) return;

    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [loginRateLimitInfo.isBlocked]);

  React.useEffect(() => {
    localStorage.setItem(LOGIN_ATTEMPT_STORAGE_KEY, JSON.stringify(loginAttemptTimes));
  }, [loginAttemptTimes]);

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    if (loginRateLimitInfo.isBlocked) {
      toast.error(
        `Too many login attempts. Please try again after ${loginRateLimitInfo.remainingSeconds} seconds.`,
      );
      return;
    }

    const { email, password } = data;

    if (email !== "" && password !== "") {
      // Check if password contains spaces
      if (/\s/.test(password)) {
        setError("password", {
          type: "manual",
          message: "Password must not contain spaces",
        });
        return; // Stop further execution
      }

      setIsSubmitting(true);

      // Handle "Remember Me" functionality
      if (localStorage.getItem("rememberme") === "true") {
        localStorage.setItem("useremail", email);
        localStorage.setItem("userpassword", btoa(password)); // Encode password in Base64
      }

      try {
        const now = Date.now();
        const recentAttempts = loginAttemptTimes.filter(
          (time) => now - time < LOGIN_RATE_LIMIT_MS,
        );

        if (recentAttempts.length >= MAX_LOGIN_ATTEMPTS) {
          const remainingMs = LOGIN_RATE_LIMIT_MS - (now - recentAttempts[0]);
          const remainingSeconds = Math.max(
            1,
            Math.ceil(remainingMs / 1000),
          );
          toast.error(
            `Too many login attempts. Please try again after ${remainingSeconds} seconds.`,
          );
          return;
        }

        const nextAttempts = [...recentAttempts, now];
        setLoginAttemptTimes(nextAttempts);

        // Login API call using RTK Query
        const apiData = await userAccountLogin(data).unwrap();

        setLoginAttemptTimes([]);
        localStorage.removeItem(LOGIN_ATTEMPT_STORAGE_KEY);

        // if (apiData.status === 200) {
        const { access, refresh } = apiData;

        // Decode the JWT token to extract user info
        // const decoded = jwtDecode(access);

        // Optional: Add checks based on decoded data
        // if (decoded?.userinfo?.approve_status === 'new') {
        //   return {}; // Handle specific approval status if required
        // }

        // Store tokens in localStorage
        localStorage.setItem("access_token", access);
        localStorage.setItem("refresh_token", refresh);
        // const url = axios.get(`${baseUrl}client-configs/current-tenant-config/`).then((res)=>{
        //   if(res){
        //     console.log("res", res)
        //     localStorage.setItem("STRIPE_PUBLISH_KEY",res?.data?.STRIPE_PUBLISH_KEY)
        //   }
        // })

        handleRedirect(password);
        // return decoded; // Return the decoded token for further use
        // }
      } catch (error) {
        setIsSubmitting(false);
        if (!error?.data?.is_verify_email) {
          toast.error(error.data.message);
        }

        const now = Date.now();
        const recentAttempts = getStoredAttemptTimes().filter(
          (time) => now - time < LOGIN_RATE_LIMIT_MS,
        );

        setLoginAttemptTimes(recentAttempts);
        // Handle API errors
        if (error?.status === 401) {
          if (error?.data?.is_verify_email) {
            // Handle unverified email
            setVerificationPopup(true);
            // setTimeout(() => {
            //   handleClosePopup();
            // }, 3000);
          } else {
            // Handle invalid credentials or other 401 errors
            setError("password", {
              type: "manual",
              message: "Invalid email or password.",
            });
          }
          clearErrors();
        } else {
          // Handle unexpected errors
          setError("form", {
            type: "manual",
            message: "An unexpected error occurred. Please try again later.",
          });
          clearErrors();
        }
      }
    }
  };

  const handleRemeberPassword = () => {
    localStorage.setItem("rememberme", !remeberPassword);
    setRememberPassword(!remeberPassword);
  };


  const redirectPath =
    new URLSearchParams(location.search).get("redirect") || "/dashboard";

  const handleRedirect = (loginPassword) => {
   
      dispatch(updateInfo()).then((res) => {
        if (res?.meta?.requestStatus === "fulfilled") {
          
          const user_data = res?.payload;
          if (user_data?.is_pwd_changed === false) {
            if (user_data?.email) {
              localStorage.setItem("forgotEmail", user_data.email);
            }
            if (loginPassword) {
              sessionStorage.setItem("pending_current_password", loginPassword);
            }
            navigate("/change-password", {
              state: { current_password: loginPassword },
            });
            return;
          }
          if (
            user_data?.role === "super_admin" &&
            user_data?.is_registered === true
          ) {
            navigate("/super-members");
          } else if (user_data?.role === "fresher") {
            navigate("/login-type");
          } else if (user_data?.is_registered) {
            if(user_data?.role === "hta" && user_data?.email){
              navigate(redirectPath === "/dashboard" ? "/programs?type=yettojoin&page=1" : redirectPath);
            }
            if (
              user_data?.userinfo?.is_questions_completed ||
              user_data.role === "admin" ||
              user_data.role === "mentee"
            ) {
              navigate(redirectPath);
            } else if (
              [user.mentor, user.event_manager].includes(user_data?.role)
            ) {
              navigate("/questions");
            }
          } else if (
            [user.mentee, user.volunteer].includes(user_data?.role) &&
            !user_data?.is_registered &&
            !user_data?.userinfo?.is_questions_completed
          ) {
            navigate(
              selectedTheme === tenants.LCA
                ? "/mentor-application-form"
                :
                selectedTheme === tenants.TIM ?
                "/questions"
                : "/individual"
            );
          } else {
            navigate("/questions");
          }
        }
      });

  };

  React.useEffect(() => {
    // const rememberme = localStorage.getItem("rememberme");
    const email = localStorage.getItem("useremail");
    const password = localStorage.getItem("userpassword");
    dispatch(resetUserInfo());
    if (localStorage.getItem("rememberme") === "true") {
      setRememberPassword(true);
      reset({ email, password: atob(password) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // if (!userData.loading && userData.status === userStatus.login) {
    //   setTimeout(() => {
    //     handleRedirect();
    //   }, 20);
    // }

    if (!userData.loading && userData.status === userStatus.pending) {
      setTimeout(() => {
        dispatch(updateUserInfo({ status: "" }));
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Prevent Enter key from triggering forgot password action
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "forgot-password") {
      e.preventDefault();
    }
  };

  const showLoadingBackdrop = isSubmitting;

  return (
    <React.Fragment>
      <style>
        {`
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
        `}
      </style>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoadingBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Right Section */}
      <div className="w-full flex items-center justify-center">
        <div className="w-4/5 max-w-md">
        
          {[ tenants.VMS ].includes(selectedTheme) && (
            <div className="flex items-center justify-center">
              <div className="flex gap-x-3 items-center mb-2">
                <img
                  className={"w-[300px] h-[100px]"}
                  src={SchedulerLogo}
                  alt="SchedulerLogo"
                ></img>
              </div>
            </div>
          )}

            <h2 className="text-center text-2xl font-semibold text-font-primary-gray mt-4 mb-10">
              Welcome to Scheduler360
            </h2>

          {/* <div className="flex justify-center items-center text-center">
            <p className="text-sm text-gray-600 mb-2">
              New User{" "}
              <span
                onClick={() => navigate("/register")}
                className="text-font-primary-main font-semibold underline cursor-pointer"
              >
                Sign up
              </span>
            </p>
          </div> */}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
           
            <div className="my-2 flex items-center before:mt-0.5 before:flex-1 before:border-t before:border-neutral-300 after:mt-0.5 after:flex-1 after:border-t after:border-neutral-300 dark:before:border-neutral-500 dark:after:border-neutral-500">
              <p
                className="mx-4 mb-0 text-center font-semibold"
                style={{
                  color: "#232323",
                }}
              >
                Registered User Login
              </p>
            </div>
            {userData.error !== "" ? (
              <div className="pb-7">
                <p className="error" role="alert">
                  {userData.error}
                </p>
              </div>
            ) : null}
            {LoginFields.map((field, index) => (
              <div className="relative mb-3" key={index}>
                <label className="block tracking-wide text-gray-700 text-xs mb-2">
                  {field.label}
                </label>
                <input
                  aria-label="login-fields"
                  type={
                    field.fieldtype === "password"
                      ? passwordVisibility
                        ? "text"
                        : field.fieldtype
                      : field.fieldtype
                  }
                  autoComplete="off"
                  data-lpignore="true" // LastPass ignore
                  // Safari-specific attribute
                  webkit-autocomplete="off"
                  className={`w-full rounded px-3 py-[0.32rem] text-[14px] focus:outline-none focus:ring-0 focus:border-background-primary-main border leading-[2.15] h-[60px] ${
                    errors[field.name]
                      ? "focus:border-teal focus:outline-none focus:ring-0 "
                      : ""
                  }`}
                  placeholder={field.placeholder}
                  style={{
                    color: "#232323",
                    border: `1px solid ${
                      errors[field.name] ? "rgb(239 68 68)" : "#3E3E3E"
                    }`,
                  }}
                  {...register(field.name, field.inputRules)}
                  aria-invalid={errors[field.name] ? "true" : "false"}
                />
                {field.fieldtype === "password" && (
                  <button
                    type="button"
                    className="absolute top-8 end-0 p-3.5 rounded-e-md"
                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                    aria-label="View password"
                  >
                    {passwordVisibility ? <EyeOpenIcon /> : <EyeCloseIcon />}
                  </button>
                )}
                {errors[field.name] && (
                  <p className="error" role="alert">
                    {errors[field.name].message}
                  </p>
                )}
              </div>
            ))}

            <div className="mb-3 flex items-center justify-between">
              <FormControlLabel
                checked={remeberPassword}
                onChange={handleRemeberPassword}
                control={<Checkbox size="small" />}
                label={"Remember me"}
                slotProps={{
                  typography: {
                    className: "text-xs font-normal",
                  },
                }}
                sx={{ mr: 1, fontSize: 14 }}
                aria-label="remember-me-checkbox"
              />
              <button
                type="button" // Set type to 'button' to prevent it from submitting the form
                className={`text-[12px] font-semibold text-background-primary-main `}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </button>
            </div>

            <div className="login-button flex justify-center text-center lg:text-left relative !mb-3">
              <Button
                dis
                type="submit"
                className={` w-1/3`}
                data-twe-ripple-init
                data-twe-ripple-color="light"
                disabled={showLoadingBackdrop || loginRateLimitInfo.isBlocked}
              >
                {loginRateLimitInfo.isBlocked
                  ? `Locked (${loginRateLimitInfo.remainingSeconds}s)`
                  : "Login"}
              </Button>
            </div>
            {/* <div className=" flex justify-center">
              <Button
                btnCls="w-[30%] sm:w-[30%] md:w-[20%] lg:w-[15%] xl:w-[15%]"
                variant="contained"
                className="!text-font-primary-main underline bg-transparent"
                onClick={() => setOpenDonationPopup(true)}
              >
                <span className="flex items-center justify-center gap-1">
                  <DonateNow fontSize="small" />
                  Donate Now!
                </span>
              </Button>
            </div> */}
          </form>
        </div>
        {/* <MuiCustomModal sx={{ zIndex: () => 1 }} open={openDonationPopup}>
          <div className="bg-white">
            <Box>
              <Box className="flex items-center justify-between mb-8">
                <Typography className="!text-font-primary-gray font-semibold text-[18px]">
                  Donate Now
                </Typography>
                <RedCloseIcon
                  className="cursor-pointer"
                  onClick={() => setOpenDonationPopup(false)}
                />
              </Box>
              <LoginDonation />
            </Box>
          </div>
        </MuiCustomModal> */}
        <MuiCustomModal
          open={verificationPopup}
          onClick={handleClosePopup}
          handleClose={handleClosePopup}
          PaperProps={{
            sx: {
              minHeight: 300,
            },
          }}
          maxWidth="sm"
          dialogTitle={"Message"}
        >
          <div className="flex justify-center items-center flex-col gap-y-4">
            <ModalTickIcon />
            <div className="flex justify-center items-center flex-col gap-2">
              <p className="text-[22px] text-font-primary-main font-bold">
                Thank You!
              </p>
              <p className="text-[16px] text-font-primary-main font-semibold text-center mt-8">
                Please verify your email address. A verification link has been{" "}
                <br /> sent to your registered email address.
              </p>
            </div>
          </div>
        </MuiCustomModal>
      </div>
    </React.Fragment>
  );
};

export { Login };
